import {
  Activity,
  LearnerAnalyticsResponse,
} from "api/resources/analytics/types"
import { DeliveryMode } from "api/resources/learningItems/types"
import { useParams } from "react-router-dom"

export function useTypeAndSlug() {
  const { slug, parentSlug, learningItemType } = useParams() as {
    slug: string
    parentSlug?: string
    learningItemType?: string
  }

  if (parentSlug) {
    return { type: "course", slug: slug }
  }

  return { type: learningItemType, slug }
}

export const formatTime = (seconds: number | null | undefined): string => {
  if (seconds === 0) return "0m"
  if (!seconds) return "-"

  if (seconds <= 3540) {
    //3540 == 59m
    return `${Math.round(seconds / 60)}m`
  }

  const hours = seconds / 3600

  if (hours < 10) {
    const fixed = hours.toFixed(1)
    return fixed.endsWith(".0") ? `${Math.round(hours)}h` : `${fixed}h`
  }

  return `${Math.round(hours)}h`
}

export const mapActivityData = ({
  data,
  deliveryMode,
}: {
  data: LearnerAnalyticsResponse | undefined
  deliveryMode: DeliveryMode
}): Activity[] => {
  const activity: Activity[] = [
    {
      label: "Milestone",
      value: data?.milestones?.completed ?? 0,
      total: data?.milestones?.total ?? 0,
      color: "#60A5FA",
    },
    {
      label: "Assignments",
      value: data?.assignments?.completed ?? 0,
      total: data?.assignments?.total ?? 0,
      color: "#97BC62",
    },
    {
      label: "Assessments",
      value: data?.scheduledAssessments?.passed ?? 0,
      total: data?.scheduledAssessments?.total ?? 0,
      color: "#9A7BBA",
    },
  ]

  const liveClasses: Activity = {
    label: "Classes",
    value: data?.liveClasses?.completed ?? 0,
    total: data?.liveClasses?.total ?? 0,
    color: "#EAB308",
  }

  const recordedClasses: Activity = {
    label: "Classes",
    value: data?.recordedClasses?.completed ?? 0,
    total: data?.recordedClasses?.total ?? 0,
    color: "#EAB308",
  }

  if (
    deliveryMode === DeliveryMode.EXECUTIVE ||
    deliveryMode === DeliveryMode.TDC
  ) {
    activity.splice(1, 0, liveClasses)
  } else if (deliveryMode === DeliveryMode.PROFESSIONAL) {
    activity.splice(1, 0, recordedClasses)
  } else if (deliveryMode === DeliveryMode.LIVE_INSTRUCTOR_LED) {
    activity.splice(1, 0, liveClasses)
  }

  return activity
}

export const getAssessmentProgressBarData = ({
  analyticsData,
  deliveryMode,
}: {
  analyticsData: LearnerAnalyticsResponse | undefined
  deliveryMode: DeliveryMode
}) => {
  const total = analyticsData?.scheduledAssessments?.total || 0
  const passed = analyticsData?.scheduledAssessments?.passed || 0
  const failed = analyticsData?.scheduledAssessments?.failed || 0
  const missed = analyticsData?.scheduledAssessments?.missed || 0
  const due = Math.max(0, total - failed - passed)

  if (
    deliveryMode === DeliveryMode.PROFESSIONAL ||
    deliveryMode === DeliveryMode.STARTER
  ) {
    return {
      title: "Assessments",
      totalProgress: total,
      completed: passed,
      items: [
        { label: "Due", value: due, color: "bg-warning-400" },
        { label: "Completed", value: passed, color: "bg-success-500" },
      ],
    }
  }

  return {
    title: "Assessments",
    totalProgress: total,
    completed: passed,
    items: [
      { label: "Due", value: due, color: "bg-warning-400" },
      { label: "Missed", value: missed, color: "bg-critical-500" },
      { label: "Completed", value: passed, color: "bg-success-500" },
    ],
  }
}

export const renderAnalyticsMetric = (
  value: number | null | undefined,
  render: ({
    isNum,
    formattedValue,
  }: {
    isNum: boolean
    formattedValue: string | number
  }) => React.ReactNode
) => {
  const isNum = typeof value === "number"
  const formattedValue = isNum
    ? value % 1 === 0 // Check if the number is an integer or decimal
      ? value
      : value.toFixed(1)
    : "_"
  return render({ isNum, formattedValue })
}
